import { motion } from 'framer-motion';
import { 
  Users, ArrowRight, CheckCircle, Mail, 
  MessageSquare, Calendar, Search, Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const tasks = [
  { icon: Mail, text: "Gerer tes emails" },
  { icon: MessageSquare, text: "Repondre aux messages clients" },
  { icon: Calendar, text: "Organiser les rendez-vous" },
  { icon: Search, text: "Aider a la prospection" },
  { icon: Briefcase, text: "Preparer les dossiers" },
];

const plans = [
  {
    name: 'Assistant Mi-Temps',
    hours: '20h/semaine',
    price: '499',
    features: ['Gestion emails', 'Rappels et relances', 'Support client', 'Taches administratives'],
  },
  {
    name: 'Assistant Dedie',
    hours: '40h/semaine',
    price: '949',
    features: ['Tout Mi-Temps', 'Marketing reseaux sociaux', 'SEO et blog', 'Gestion complete'],
    popular: true,
  },
  {
    name: 'Croissance',
    hours: '2 assistants',
    price: '1499',
    features: ['Tout Dedie', 'Assistant commercial', 'Prospection active', 'Machine a leads'],
  },
];

export default function AssistantsPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Assistants Dédiés | Délégation Administrative pour Artisans - BatiAssist"
        description="Délègue tes tâches administratives à un assistant dédié : gestion emails, relances clients, organisation planning. Mi-temps ou temps plein selon tes besoins."
        keywords="assistant administratif artisan, délégation tâches BTP, secrétaire virtuelle bâtiment, aide gestion entreprise, assistant mi-temps"
        path="/fonctionnalites/assistants"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-600 text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Users className="w-4 h-4" />
              Assistants Dedies
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Un assistant{' '}
              <span className="text-ba-secondary">pour ton entreprise</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Des assistants humains formes a ton métier qui t'aident au quotidien. 
              Libère-toi des taches administratives pour toi concentrer sur ton cœur de metier.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Decouvrir les assistants
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* What they do */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Ce que nos assistants peuvent faire pour toi
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des assistants polyvalents qui s'adaptent a tes besoins
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tasks.map((task, index) => {
              const Icon = task.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-6 bg-slate-50 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-slate-700 font-medium">{task.text}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Trois niveaux d'accompagnement
            </h2>
            <p className="text-lg text-slate-600">
              Choisis la formule adaptee a tes besoins
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-3xl p-8 ${plan.popular ? 'ring-2 ring-ba-secondary shadow-xl' : 'border border-slate-200'}`}
              >
                {plan.popular && (
                  <span className="inline-block px-3 py-1 bg-ba-secondary text-white text-xs font-bold uppercase rounded-full mb-4">
                    Recommande
                  </span>
                )}
                <h3 className="text-xl font-bold text-slate-800 mb-2">{plan.name}</h3>
                <p className="text-slate-500 mb-4">{plan.hours}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-800">{plan.price}€</span>
                  <span className="text-slate-500">/mois</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${plan.popular ? 'text-ba-secondary' : 'text-ba-success'}`} />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Button className={`w-full rounded-full py-6 ${plan.popular ? 'bg-ba-secondary hover:bg-ba-secondary/90 text-white' : 'bg-slate-800 hover:bg-slate-700 text-white'}`}>
                    Choisir cette formule
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefit */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Concentre-toi sur ton métier
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Pendant que ton assistant gere les emails, les relances et l'administratif, 
                tu peux te concentrer sur ce que tu fais de mieux : ton travail sur le terrain.
              </p>
              <div className="space-y-4">
                {['Plus de temps pour tes chantiers', 'Moins de stress administratif', 'Meilleure relation client', 'Activite mieux organisee'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-ba-success" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-3xl p-8 text-white"
            >
              <Users className="w-12 h-12 mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">Assistants formes a ton métier</h3>
              <p className="text-white/80 leading-relaxed">
                Nos assistants comprennent les specificites du batiment. Ils savent ce qu'est un devis, 
                une facture, un chantier. Pas besoin de tout leur expliquer.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-purple-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Libère-toi des taches administratives
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Découvre comment un assistant peut transformer ton quotidien.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-purple-600 font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Decouvrir les assistants
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
